import React from 'react';
import { useLanguage } from '@/contexts/LanguageContext';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { CheckCircle, Clock, Truck, Package, Phone, ChevronRight, ClipboardList, MapPin, Calculator, MessageSquare } from 'lucide-react';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import SEO from '@/components/SEO';
import InteractiveMap from '@/components/InteractiveMap';
import AIReadabilityBlock from '@/components/AIReadabilityBlock';
import { trackPhoneClick, trackQuoteButtonClick } from '@/lib/analytics';

const ServicesPage = () => {
  const { language } = useLanguage();
  const isEs = language === 'es';

  const services = [
    {
      key: 'sod-delivery',
      title: isEs ? 'Entrega de Césped (Sod Delivery)' : 'Sod Delivery Service',
      description: isEs
        ? 'Césped recién cortado del campo, entregado en palets a residencias y proyectos comerciales en Magnolia, The Woodlands, Conroe, Cypress, Spring, Tomball y todo el Condado de Montgomery, TX.'
        : 'Farm-fresh cut sod delivered by the pallet to homes and commercial projects across Magnolia, The Woodlands, Conroe, Cypress, Spring, Tomball and all of Montgomery County, TX.',
      icon: Truck,
      href: isEs ? '/es/servicios/entrega-cesped' : '/en/services/sod-delivery',
      features: isEs
        ? ['Bermuda, St. Augustine, Zoysia y más', 'Palets completos o medios palets', 'Entrega programada por ruta', 'Asesoría de preparación de terreno']
        : ['Bermuda, St. Augustine, Zoysia and more', 'Full pallets or half pallets', 'Route-scheduled delivery', 'Site prep guidance included'],
      price: isEs ? 'Cotización según variedad y volumen' : 'Quoted by variety and volume',
      timing: isEs ? 'Corte reciente, entrega rápida' : 'Freshly cut, fast delivery'
    },
    {
      key: 'bulk-delivery',
      title: isEs ? 'Entrega a Granel (Bulk Delivery)' : 'Bulk Materials Delivery',
      description: isEs
        ? 'Entrega a granel de tierra vegetal, mulch, roca decorativa, grava y arena para paisajistas, contratistas y propietarios en el área de Magnolia y el norte de Houston.'
        : 'Bulk delivery of topsoil, mulch, decorative rock, gravel and sand for landscapers, contractors and homeowners across Magnolia and the north Houston area.',
      icon: Package,
      href: isEs ? '/es/servicios/entrega-granel' : '/en/services/bulk-delivery',
      features: isEs
        ? ['Cantidades por yarda cúbica', 'Precios por volumen', 'Descarga en múltiples puntos', 'Camiones dedicados para bulk']
        : ['Priced per cubic yard', 'Volume discounts available', 'Multiple dump locations', 'Dedicated bulk trucks'],
      price: isEs ? 'Precio por volumen disponible' : 'Volume pricing available',
      timing: isEs ? 'Entrega típica en 24-48 horas' : 'Typical 24-48 hour delivery'
    },
  ];

  const materials = isEs
    ? [
        { name: 'Césped en palet', href: '/es/pasto' },
        { name: 'Roca decorativa y grava', href: '/es/roca' },
        { name: 'Mulch y mantillo', href: '/es/mulch' },
        { name: 'Tierra vegetal y compost', href: '/es/tierra' },
        { name: 'Arena de construcción', href: '/es/arena' },
        { name: 'Ver todos los productos', href: '/es/productos' },
      ]
    : [
        { name: 'Pallet sod', href: '/en/grass' },
        { name: 'Decorative rock & gravel', href: '/en/rock' },
        { name: 'Mulch varieties', href: '/en/mulch' },
        { name: 'Topsoil & compost', href: '/en/soil' },
        { name: 'Construction sand', href: '/en/sand' },
        { name: 'Browse all products', href: '/en/products' },
      ];

  const processSteps = isEs
    ? [
        { title: '1. Elige tus materiales', body: 'Explora sod, mulch, roca, tierra o arena y decide cantidades con nuestra calculadora.', icon: ClipboardList },
        { title: '2. Solicita tu cotización', body: 'Usa la calculadora online o llámanos al (936) 320-0763 con la dirección de entrega.', icon: Calculator },
        { title: '3. Confirmamos ruta y fecha', body: 'Programamos la entrega según tu zona (Magnolia, Woodlands, Conroe, Cypress, Spring, Tomball).', icon: MapPin },
        { title: '4. Descarga y listo', body: 'Descargamos en el punto acordado. Recibes material fresco listo para instalar.', icon: Truck },
      ]
    : [
        { title: '1. Choose your materials', body: 'Browse sod, mulch, rock, soil or sand and size quantities with our calculator.', icon: ClipboardList },
        { title: '2. Request your quote', body: 'Use the online calculator or call (936) 320-0763 with your delivery address.', icon: Calculator },
        { title: '3. We confirm route & date', body: 'Delivery is scheduled by service zone (Magnolia, Woodlands, Conroe, Cypress, Spring, Tomball).', icon: MapPin },
        { title: '4. Drop-off and done', body: 'We drop the material at the agreed location. Fresh product, ready to install.', icon: Truck },
      ];

  const faqs = isEs
    ? [
        { q: '¿Qué áreas cubren para entrega?', a: 'Entregamos sod y materiales a granel en Magnolia, The Woodlands, Conroe, Cypress, Spring, Tomball, Montgomery, Pinehurst y comunidades cercanas del Condado de Montgomery, TX. Para direcciones fuera del área contáctenos para confirmar tarifa.' },
        { q: '¿Cuánto cuesta la entrega?', a: 'El costo de entrega depende de las millas de ida y vuelta desde 6920 FM 1488, Magnolia, TX 77354 y del tipo de camión (palet de césped vs. granel). Al usar la calculadora verás la tarifa exacta antes de confirmar.' },
        { q: '¿Cuál es el pedido mínimo?', a: 'Trabajamos por palet completo o medio palet en césped, y por yardas cúbicas en granel. No hay un mínimo estricto en dólares, pero la tarifa de entrega hace más eficiente pedir varios materiales en una sola ruta.' },
        { q: '¿En cuánto tiempo entregan?', a: 'La mayoría de entregas de sod y granel se programan en 24 a 48 horas hábiles según la ruta del día. Recomendamos confirmar tu pedido temprano si necesitas un día específico.' },
        { q: '¿Ofrecen instalación de césped?', a: 'Nuestro servicio principal es entrega de sod fresco y materiales a granel. Podemos recomendar contratistas locales de confianza para instalación si lo necesitas.' },
        { q: '¿Puedo recoger material en su ubicación?', a: 'Sí, hay recogida disponible con cita en 6920 FM 1488, Magnolia, TX 77354. Llámanos al (936) 320-0763 para confirmar disponibilidad y horario.' },
      ]
    : [
        { q: 'What areas do you deliver to?', a: 'We deliver sod and bulk landscaping materials across Magnolia, The Woodlands, Conroe, Cypress, Spring, Tomball, Montgomery, Pinehurst and neighboring communities in Montgomery County, TX. Contact us for out-of-area addresses to confirm the delivery fee.' },
        { q: 'How is delivery priced?', a: 'Delivery is priced by roundtrip miles from 6920 FM 1488, Magnolia, TX 77354 and the truck type (sod pallet vs. bulk dump). Our online calculator shows the exact delivery fee before you confirm.' },
        { q: 'Is there a minimum order?', a: 'We deliver sod by full or half pallets and bulk material by the cubic yard. There is no strict dollar minimum, but combining several materials in one route keeps delivery cost efficient.' },
        { q: 'How fast can you deliver?', a: 'Most sod and bulk deliveries are scheduled within 24 to 48 business hours depending on the day\'s route. Book early if you need a specific delivery date.' },
        { q: 'Do you also install sod?', a: 'Our core service is delivering fresh cut sod and bulk landscaping materials. We can recommend trusted local installers if you need turnkey installation.' },
        { q: 'Can I pick up materials in person?', a: 'Yes. Pickup is available by appointment at 6920 FM 1488, Magnolia, TX 77354. Call (936) 320-0763 to confirm hours and availability.' },
      ];

  const seoTitle = isEs
    ? 'Servicios de Entrega de Césped y Materiales | Magnolia TX'
    : 'Sod & Bulk Material Delivery Services | Magnolia TX';

  const seoDescription = isEs
    ? 'Servicios de entrega de sod y materiales a granel (tierra, mulch, roca, arena) en Magnolia, The Woodlands, Conroe, Cypress y todo el Condado de Montgomery, TX. Cotización rápida.'
    : 'Sod delivery and bulk material services (topsoil, mulch, rock, sand) in Magnolia, The Woodlands, Conroe, Cypress and all of Montgomery County, TX. Fast quotes, farm-fresh sod.';

  const seoKeywords = isEs
    ? 'entrega de sod texas, entrega de cesped magnolia tx, entrega granel montgomery county, mulch delivery texas, roca decorativa magnolia'
    : 'sod delivery texas, sod delivery magnolia tx, bulk material delivery montgomery county, mulch delivery texas, decorative rock delivery magnolia';

  const canonicalPath = isEs ? '/es/servicios' : '/en/services';
  const baseUrl = 'https://thegrassdepotllc.com';

  const serviceSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": seoTitle,
    "description": seoDescription,
    "serviceType": ["Sod Delivery", "Bulk Landscaping Material Delivery"],
    "provider": {
      "@type": "LocalBusiness",
      "name": "The Grass Depot LLC",
      "telephone": "+1-936-320-0763",
      "address": {
        "@type": "PostalAddress",
        "streetAddress": "6920 FM 1488",
        "addressLocality": "Magnolia",
        "addressRegion": "TX",
        "postalCode": "77354",
        "addressCountry": "US"
      }
    },
    "areaServed": [
      "Magnolia, TX", "The Woodlands, TX", "Conroe, TX", "Cypress, TX",
      "Spring, TX", "Tomball, TX", "Montgomery, TX", "Pinehurst, TX"
    ],
    "url": `${baseUrl}${canonicalPath}`
  };

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map(f => ({
      "@type": "Question",
      "name": f.q,
      "acceptedAnswer": { "@type": "Answer", "text": f.a }
    }))
  };

  return (
    <div className="min-h-screen bg-gradient-subtle">
      <SEO
        title={seoTitle}
        description={seoDescription}
        keywords={seoKeywords}
        canonical={canonicalPath}
        structuredData={[serviceSchema, faqSchema]}
        language={language}
        breadcrumbs={[
          { name: isEs ? 'Inicio' : 'Home', url: `/${language}` },
          { name: isEs ? 'Servicios' : 'Services', url: canonicalPath },
        ]}
      />
      <Header />

      <main>
      {/* Hero Section */}
      <section className="relative py-20 bg-gradient-hero text-white overflow-hidden">
        <div className="absolute inset-0 opacity-10 animate-pulse"></div>
        <div className="container mx-auto px-6 text-center relative">
          <div className="animate-fade-in">
            <h1 className="text-4xl md:text-6xl font-bold mb-6">
              {isEs ? 'Servicios de Entrega de Césped y Materiales de Paisajismo' : 'Sod & Landscaping Material Delivery Services'}
            </h1>
            <p className="text-xl md:text-2xl text-white/90 max-w-3xl mx-auto mb-8 animate-fade-in delay-200">
              {isEs
                ? 'Entregamos sod fresco y materiales a granel en Magnolia, The Woodlands, Conroe, Cypress, Spring, Tomball y todo el Condado de Montgomery, TX.'
                : 'Fresh sod and bulk landscaping materials delivered across Magnolia, The Woodlands, Conroe, Cypress, Spring, Tomball and all of Montgomery County, TX.'}
            </p>
            <div className="flex flex-wrap justify-center gap-4 animate-fade-in delay-400">
              <Badge variant="secondary" className="bg-white/20 text-white border-white/30 px-4 py-2 text-base hover:bg-white/30 transition-all hover-scale">
                <CheckCircle className="h-5 w-5 mr-2" />
                {isEs ? 'Recién Cortado del Campo' : 'Fresh Cut from Farm'}
              </Badge>
              <Badge variant="secondary" className="bg-white/20 text-white border-white/30 px-4 py-2 text-base hover:bg-white/30 transition-all hover-scale">
                <Clock className="h-5 w-5 mr-2" />
                {isEs ? 'Entrega Rápida 24-48 h' : 'Fast 24-48 h Delivery'}
              </Badge>
            </div>
          </div>
        </div>
      </section>

      {/* SEO intro */}
      <section className="py-12 bg-background">
        <div className="container mx-auto px-6 max-w-4xl">
          <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-4">
            {isEs ? 'Nuestros servicios de entrega de materiales' : 'Our Landscaping Material Delivery Services'}
          </h2>
          <p className="text-lg text-muted-foreground leading-relaxed mb-4">
            {isEs
              ? 'The Grass Depot LLC es un proveedor local de sod y materiales de paisajismo con base en 6920 FM 1488, Magnolia, TX 77354. Trabajamos con propietarios, paisajistas y contratistas del norte de Houston que necesitan césped fresco entregado en palet o materiales a granel como tierra vegetal, mulch, roca y arena.'
              : 'The Grass Depot LLC is a local sod and landscaping material supplier based at 6920 FM 1488, Magnolia, TX 77354. We serve homeowners, landscapers and contractors across the north Houston area who need fresh pallet sod or bulk materials like topsoil, mulch, decorative rock and sand.'}
          </p>
          <p className="text-lg text-muted-foreground leading-relaxed">
            {isEs
              ? 'Combinamos entrega de sod y entrega a granel en la misma ruta para ahorrar tiempo y costo. Puedes pedir cotización online con nuestra calculadora, llamar al (936) 320-0763 o escribirnos desde la página de contacto.'
              : 'We combine sod delivery and bulk delivery on the same route to save you time and cost. Request a quote online with our calculator, call (936) 320-0763, or reach us from the contact page.'}
          </p>
        </div>
      </section>

      {/* AI Readability Block - Optimized for LLM extraction */}
      <AIReadabilityBlock variant="services" />

      {/* Services Grid */}
      <section className="py-20">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
              {isEs ? 'Servicios profesionales de entrega' : 'Professional Delivery Services'}
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              {isEs
                ? 'Elige entrega de sod o entrega a granel. Ambos servicios se coordinan desde nuestra ubicación en Magnolia, TX.'
                : 'Choose sod delivery or bulk delivery. Both are dispatched from our Magnolia, TX location.'}
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service) => {
              const IconComponent = service.icon;
              return (
                <Card key={service.key} className="group hover:shadow-glow transition-all duration-300 border-2 hover:border-primary">
                  <CardHeader className="text-center pb-4">
                    <div className="mx-auto w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mb-4 group-hover:bg-primary/20 transition-colors">
                      <IconComponent className="h-8 w-8 text-primary" />
                    </div>
                    <CardTitle className="text-xl font-bold text-foreground group-hover:text-primary transition-colors">
                      {service.title}
                    </CardTitle>
                    <CardDescription className="text-muted-foreground">
                      {service.description}
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      <div className="flex justify-between items-center">
                        <span className="text-sm font-medium text-muted-foreground">
                          {isEs ? 'Precio:' : 'Pricing:'}
                        </span>
                        <span className="text-sm font-semibold text-primary text-right">{service.price}</span>
                      </div>

                      <div className="flex items-center text-sm text-muted-foreground">
                        <Clock className="h-4 w-4 mr-2 text-primary" />
                        {service.timing}
                      </div>

                      <ul className="space-y-2">
                        {service.features.map((feature, index) => (
                          <li key={index} className="flex items-center text-sm text-muted-foreground">
                            <CheckCircle className="h-4 w-4 text-primary mr-2 flex-shrink-0" />
                            {feature}
                          </li>
                        ))}
                      </ul>

                      <Link to={service.href} aria-label={`${service.title} — ${isEs ? 'ver detalles' : 'view details'}`}>
                        <Button variant="outline" className="w-full group-hover:bg-primary group-hover:text-primary-foreground transition-colors">
                          {service.key === 'sod-delivery'
                            ? (isEs ? 'Ver detalles de entrega de sod' : 'View sod delivery details')
                            : (isEs ? 'Ver detalles de entrega a granel' : 'View bulk delivery details')}
                          <ChevronRight className="ml-2 h-4 w-4" />
                        </Button>
                      </Link>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* How our delivery process works */}
      <section className="py-16 bg-background">
        <div className="container mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
              {isEs ? 'Cómo funciona nuestro proceso de entrega' : 'How Our Delivery Process Works'}
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              {isEs
                ? 'Cuatro pasos simples desde la cotización hasta la descarga en tu proyecto.'
                : 'Four simple steps from quote to drop-off at your project site.'}
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {processSteps.map((step, i) => {
              const Icon = step.icon;
              return (
                <Card key={i} className="p-6 text-center">
                  <div className="mx-auto w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mb-4">
                    <Icon className="h-6 w-6 text-primary" />
                  </div>
                  <h3 className="text-lg font-semibold text-foreground mb-2">{step.title}</h3>
                  <p className="text-sm text-muted-foreground">{step.body}</p>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Materials we deliver — internal links */}
      <section className="py-16">
        <div className="container mx-auto px-6 max-w-5xl">
          <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4 text-center">
            {isEs ? 'Materiales que entregamos' : 'Materials We Deliver'}
          </h2>
          <p className="text-lg text-muted-foreground text-center mb-8 max-w-2xl mx-auto">
            {isEs
              ? 'Todos nuestros productos están disponibles para entrega o recogida en Magnolia, TX.'
              : 'Every product below is available for delivery or pickup from our Magnolia, TX yard.'}
          </p>
          <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-3">
            {materials.map((m) => (
              <Link
                key={m.href}
                to={m.href}
                className="flex items-center justify-between p-4 rounded-lg border bg-card hover:border-primary hover:shadow-card transition-all"
              >
                <span className="font-medium text-foreground">{m.name}</span>
                <ChevronRight className="h-4 w-4 text-primary" />
              </Link>
            ))}
          </div>
          <div className="text-center mt-8">
            <Link
              to={isEs ? '/es/cotizar' : '/en/quote'}
              onClick={() => trackQuoteButtonClick('services_materials_section')}
              data-event-name="quote_button_click"
              data-source="services_materials_section"
            >
              <Button variant="hero" size="lg">
                <Calculator className="mr-2 h-5 w-5" />
                {isEs ? 'Calcular y solicitar cotización' : 'Calculate and request a quote'}
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Interactive Service Areas Map */}
      <InteractiveMap />

      {/* FAQ Section */}
      <section className="py-16 bg-background">
        <div className="container mx-auto px-6 max-w-4xl">
          <div className="text-center mb-10">
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
              {isEs ? 'Preguntas frecuentes sobre entrega' : 'Delivery Frequently Asked Questions'}
            </h2>
            <p className="text-lg text-muted-foreground">
              {isEs
                ? 'Respuestas rápidas sobre cobertura, precios y tiempos de entrega.'
                : 'Quick answers on coverage, pricing and delivery timing.'}
            </p>
          </div>
          <div className="space-y-4">
            {faqs.map((f, i) => (
              <Card key={i} className="p-6">
                <h3 className="text-lg font-semibold text-foreground mb-2">{f.q}</h3>
                <p className="text-muted-foreground leading-relaxed">{f.a}</p>
              </Card>
            ))}
          </div>
          <div className="mt-10 text-center">
            <p className="text-muted-foreground mb-4">
              {isEs ? '¿Otra pregunta? Escríbenos o llámanos.' : 'Another question? Send us a message or call.'}
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Link to={isEs ? '/es/contacto' : '/en/contact'}>
                <Button variant="outline" size="lg">
                  <MessageSquare className="mr-2 h-5 w-5" />
                  {isEs ? 'Ir a contacto' : 'Go to contact'}
                </Button>
              </Link>
              <a
                href="tel:9363200763"
                data-event-name="phone_click"
                data-source="services_faq"
                onClick={() => trackPhoneClick('services_faq')}
              >
                <Button variant="hero" size="lg">
                  <Phone className="mr-2 h-5 w-5" />
                  {isEs ? 'Llamar (936) 320-0763' : 'Call (936) 320-0763'}
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-secondary text-secondary-foreground">
        <div className="container mx-auto px-6 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            {isEs ? '¿Listo para pedir tu entrega?' : 'Ready to schedule your delivery?'}
          </h2>
          <p className="text-lg text-secondary-foreground/80 mb-8 max-w-2xl mx-auto">
            {isEs
              ? 'Obtén una cotización clara con costo de entrega incluido. Sin sorpresas.'
              : 'Get a clear quote with delivery cost included. No surprises.'}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to={isEs ? '/es/cotizar' : '/en/quote'}
              onClick={() => trackQuoteButtonClick('services_bottom_cta')}
              data-event-name="quote_button_click"
              data-source="services_bottom_cta"
            >
              <Button variant="hero" size="lg">
                {isEs ? 'Obtener cotización gratis' : 'Get a free quote'}
              </Button>
            </Link>
            <a
              href="tel:9363200763"
              data-event-name="phone_click"
              data-source="services_bottom_cta"
              onClick={() => trackPhoneClick('services_bottom_cta')}
            >
              <Button variant="outline" size="lg" className="border-secondary-foreground/20 text-green-700 hover:bg-secondary-foreground/10">
                <Phone className="mr-2 h-5 w-5" />
                {isEs ? 'Llamar (936) 320-0763' : 'Call (936) 320-0763'}
              </Button>
            </a>
          </div>
        </div>
      </section>
      </main>

      <Footer />
    </div>
  );
};

export default ServicesPage;
